import React, { useState, useEffect } from 'react';

import Modal from 'react-modal';
import moment from 'moment';
import './kit.assign.dialog.scss';
import { PatientAvatar } from '../visuals/Patient.avatar';
import Button from '../forms/buttons/Button';

import { useLazyQuery, useMutation } from '@apollo/client';
import gqlSchemaLibrary from '../services/graphql.schemas';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { clearGqlTypename } from '../utils/clearTypename';
import {getDateFormat, getDOB} from "../utils/date";
import {useTranslation} from "react-i18next";
import {ModalLog} from "../services/interactions/elements/modal";
import AuditTrail, {PAGES} from "../services/interactions/audit.trail";

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: "none",
        padding: "0px",
        boxShadow: "0 3px 8px 0 rgba(0, 0, 0, 0.38)",
    }
};


Modal.setAppElement('#root');

const _Modal = ModalLog(Modal);

function KitAssignDialog({ currentOrg, patientToAssign, askToClose }) {
    const {t} = useTranslation(['programs','common','devices'])
    const [kitIdByOrg] = useLazyQuery(gqlSchemaLibrary.kits.query.getKitIdForOrganization, {
        onCompleted: ({ getKitIdForOrganization }) => {
            console.log('getKitIdForOrganization: ', getKitIdForOrganization);
            if (!!getKitIdForOrganization && !!getKitIdForOrganization._id) {
                setValidkit(clearGqlTypename(getKitIdForOrganization));
                setKitCandidate(clearGqlTypename(getKitIdForOrganization));
            } else {
                setKitCandidate(null);
                setValidkit(null);
                setKitInvalid(true);
            }
        },
        fetchPolicy: 'network-only'
    });

    // Execute mutation  to assign kit and notify parent on close to update patientList to show kitId
    const [_assignKitToPatient] = useMutation(gqlSchemaLibrary.kits.mutation.assignKitToPatient, {
        onCompleted: ({ assignKitToPatient }) => {
            console.log('Assign kit to patient: ', assignKitToPatient);
            setValidkit(clearGqlTypename(assignKitToPatient));
            setKitCandidate(clearGqlTypename(assignKitToPatient));
            setKitSuccess(true);
        },
        onError: (error) => {
            console.error('Error assigning kit: ', error);
            setKitError(error.message);
        }
    })

    const [kitId, setKitId] = useState('');
    const [validKitObject, setValidkit] = useState(null);
    const [kitCandidate, setKitCandidate] = useState(null);
    const [kitSuccess, setKitSuccess] = useState(false);
    const [kitError, setKitError] = useState(false);

    const [kitInvalid, setKitInvalid] = useState(false);

    // Subscribe
    const [kitSubject] = useState(new Subject());

    useEffect(() => {
        const kSubscribe = kitSubject.pipe(debounceTime(400)).subscribe((d) => {
            console.log('Looking for kit: ', d);
            kitIdByOrg({
                variables: {
                    kitId: d
                }
            })
        });
        return () => {
            kSubscribe.unsubscribe();
        }
    }, [kitSubject, kitIdByOrg]);



    const closeModal = () => {
        AuditTrail.sendLog(PAGES.PROGRAM_PARTICIPANTS, {tag: 'button', event: 'closeModal', action: 'input modal enter kit id', patient_id: patientToAssign._id});
        if(kitSuccess){
            return closeAssigned();
        }
        reset();
        askToClose(false);
    }

    const closeAssigned = () => {
        askToClose(Object.assign({}, kitCandidate));
        reset();
    }

    const reset = () => {
        setKitId('');
        setKitCandidate(null);
        setKitError(false);
        setKitSuccess(false);
    }

    const _setKitId = (e) => {
        const val = e.target.value;
        //  const spacedVal = val.split(' ').join('').split('').map((s, i) => (i === 4 || i === 8) ? ` ${s}` : s).join('');
        setKitId(val);
        kitSubject.next(val);
        setKitInvalid(false);
    }

    const _onBlur = (e) => {
        AuditTrail.sendLog(PAGES.PROGRAM_PARTICIPANTS, {tag: 'input', event: 'onBlur', action: 'input modal enter kit id', patient_id: patientToAssign._id, value: e.target.value});
    }

    const cancelCandidate = () => {
        setKitCandidate(null);
    }

    const assignKitToPatient = () => {
        _assignKitToPatient({
            variables: {
                kitId: kitCandidate.kitId,
                patientId: patientToAssign._id
            }
        })
    }

    // RENDER HANDLERS =========================

    const renderKitInput = () => {
        return (
            <>
                <div className="__AssignInput">
                    <h3>{t('assing-kit-dialog.enter-kit-id')}</h3>
                    <input className={`grandInput ${kitInvalid && kitId ? '_invalid' : ''} ${validKitObject ? '_valid' : ''}`} value={kitId} onChange={_setKitId} onBlur={_onBlur}>
                    </input>
                </div>
                <div className="_confirmDetails">
                    <div className="_confirmInner">
                        
                    </div>
                </div>
            </>
        )
    }

    const renderKitError = () => {
        return (
            <>
                <div className="__AssignSuccess">
                    <div className="__SuccessMessage">
                        <div className="__Error">
                            <i className="material-icons">
                                priority_high
                            </i>
                        </div>
                        <h3>{t('assing-kit-dialog.text-1')}</h3>
                        <span></span>
                        <p className="__footNote">{t('assing-kit-dialog.text-2')}</p>
                    </div>
                </div>
                <div className="__AssignActions two">
                    <Button className="tertiary" handleClick={closeModal}>Close</Button>
                    <Button className="primary" handleClick={reset}><b>Try again</b></Button>
                </div>
            </>
        )
    }

    const renderKitSuccess = () => {
        return (
            <>
                <div className="__AssignSuccess">
                    <div className="__SuccessMessage">
                        <div className="__Nice">
                            <i className="material-icons">
                                check
                            </i>
                        </div>
                        <h3>{('devices:kit')} {kitId} {t('assing-kit-dialog.succesfully-assigned')}</h3>
                    </div>
                    <div className="_proceed">

                    </div>
                </div>
                <div className="__AssignActions two">
                    <div></div>
                    <Button className="primary" handleClick={closeAssigned}><b>Close</b></Button>
                </div>
            </>
        )
    }

    const renderKitConfirm = () => {
        return (
            <>
                <div className="__AssignConfirm">
                    <h3>{t('assing-kit-dialog.text-3')}</h3>
                    <h2>{kitCandidate.kitId}</h2>
                    <div className="__AssignConfirmDetails">
                        <div className="_toggleDetails">
                            <i className="material-icons">
                                arrow_drop_down
                            </i>
                            <p>{t('assing-kit-dialog.kit-components')}</p>
                        </div>
                        <div className="_confirmDetails">
                            <div className="_confirmInner">
                                {
                                    kitCandidate.peripherals.map((peripheral, i) => {
                                        return (
                                            <div className="__deviceDetail" key={peripheral.deviceId}>
                                                <p className="_deviceType">
                                                    {peripheral.deviceSubtype}
                                                </p>
                                                <div className="_deviceTypeList">
                                                    <p>{peripheral.deviceId}</p>
                                                    <p>{peripheral.deviceModel}</p>
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div className="__AssignActions">
                    <span></span>
                    <div className="_SubActions">
                        <Button className="tertiary" handleClick={cancelCandidate}>{t('common:go-back')}</Button>
                        <Button className="primary" handleClick={assignKitToPatient}>{t('devices:assign')}</Button>
                    </div>
                </div>
            </>
        )
    }

    const renderPatientAssign = () => {
        if (!patientToAssign) {
            return ''
        }
        return (
            <div className="__KitAssignBody">
                <div className="__KitAssignBodyPatientBio">
                    <div className="_BioDisplay">
                        <PatientAvatar bio={patientToAssign.user.bio} small={true} />
                        <div className="__BioDisplayName">
                            <h3>
                                {patientToAssign.user.bio.lastname}, {patientToAssign.user.bio.firstname}
                            </h3>
                            <div className="__BioDisplayData">
                                {getDOB(currentOrg, patientToAssign.dateOfBirth, patientToAssign.user.bio.gender)}
                            </div>
                        </div>
                    </div>
                </div>
                {renderByState()}
            </div>
        )
    }

    const renderByState = () => {
        if (kitSuccess) {
            return renderKitSuccess()
        }
        if (kitError) {
            return renderKitError();
        }
        if (kitCandidate) {
            return renderKitConfirm();
        } else {
            return renderKitInput();
        }
    }

    return (
        <_Modal
            isOpen={!!patientToAssign}
            onRequestClose={closeModal}
            style={customStyles}
            contentLabel={t('participant-kit-assignment')}
            label="KitAssignDialog"
            event={PAGES.PROGRAM_PARTICIPANTS}
            eventParams={{patient_id: patientToAssign? patientToAssign._id : null}}
        >
            <div className="__KitAssignDialog">
                <div className="__KitAssignTitle">
                    <i className="material-icons" onClick={closeModal}>
                        arrow_back_ios
                    </i>
                    <h3>{t('devices:assign')} {!!patientToAssign && patientToAssign.kitId && patientToAssign.kitId.length ? t('assing-kit-dialog.additional-kit') : t('devices:kit-capitalize')}</h3>
                </div>
                {renderPatientAssign()}
            </div>
        </_Modal>
    )
}

export default KitAssignDialog;
